import type { LibraryPosition, PriceSample } from "./schema";

/**
 * Wählt den passendsten Einheitspreis aus der Preis-Historie einer
 * Bibliotheks-Position in Abhängigkeit von der geplanten Menge.
 *
 * Strategie:
 * 1. Wenn keine Samples vorliegen → Fallback auf ep.
 * 2. Sonst: gewichte jedes Sample mit einer Mengen-Ähnlichkeit
 *    (geometrischer Abstand in log-Raum). Der Eintrag mit der kleinsten
 *    Distanz gewinnt. Bei Mehrfach-Gleichständen wird der Median der
 *    engsten Gruppe genommen → liefert "großer Auftrag → niedriger EP,
 *    kleiner Auftrag → hoher EP" Verhalten automatisch.
 * 3. Bei menge<=0 → Median aller Samples.
 */
export function pickPriceForQuantity(
  samples: PriceSample[] | null | undefined,
  menge: number,
  fallbackEp: number,
): { ep: number; matchedMenge: number | null; source: "sample" | "median" | "fallback" } {
  const validSamples = (samples ?? []).filter(
    (s) => Number.isFinite(s.ep) && s.ep > 0 && Number.isFinite(s.menge) && s.menge >= 0,
  );
  if (validSamples.length === 0) {
    return { ep: fallbackEp, matchedMenge: null, source: "fallback" };
  }
  if (!Number.isFinite(menge) || menge <= 0) {
    const eps = validSamples.map((s) => s.ep).sort((a, b) => a - b);
    const mid = Math.floor(eps.length / 2);
    const med = eps.length % 2 === 0 ? (eps[mid - 1] + eps[mid]) / 2 : eps[mid];
    return { ep: med, matchedMenge: null, source: "median" };
  }
  const scored = validSamples
    .map((s) => {
      const a = Math.max(menge, 0.01);
      const b = Math.max(s.menge, 0.01);
      const distance = Math.abs(Math.log(a) - Math.log(b));
      return { s, distance };
    })
    .sort((x, y) => x.distance - y.distance);
  const best = scored[0];
  const close = scored.filter((x) => x.distance <= best.distance + 0.05);
  if (close.length === 1) {
    return { ep: best.s.ep, matchedMenge: best.s.menge, source: "sample" };
  }
  const eps = close.map((x) => x.s.ep).sort((a, b) => a - b);
  const mid = Math.floor(eps.length / 2);
  const med = eps.length % 2 === 0 ? (eps[mid - 1] + eps[mid]) / 2 : eps[mid];
  return { ep: med, matchedMenge: best.s.menge, source: "sample" };
}

/** Normalisierter Lookup-Key: lowercased name + unit. */
export function libraryLookupKey(name: string, unit: string): string {
  return `${name.trim().toLowerCase().replace(/\s+/g, " ")}::${unit.trim().toLowerCase()}`;
}

export function findLibraryMatchByKey(
  positions: LibraryPosition[],
  name: string,
  unit: string,
): LibraryPosition | null {
  const key = libraryLookupKey(name, unit);
  for (const p of positions) {
    if (libraryLookupKey(p.name, p.unit) === key) return p;
  }
  const nameKey = name.trim().toLowerCase().replace(/\s+/g, " ");
  for (const p of positions) {
    if (p.name.trim().toLowerCase().replace(/\s+/g, " ") === nameKey) return p;
  }
  return null;
}

/** Schwellwert für „mögliche" Duplikate: relative EP-Spanne. */
export const DUPLICATE_EP_TOLERANCE = 0.10;

export type DuplicateGroupKind = "exact" | "possible";

export interface DuplicateGroupEntry {
  position: LibraryPosition;
  sampleCount: number;
}

export interface DuplicateGroup {
  /** Stabile Gruppen-ID (Sektion + lookup-key). */
  id: string;
  kind: DuplicateGroupKind;
  sektionId: string;
  /** Repräsentative (gekürzt-normalisierte) Bezeichnung. */
  name: string;
  unit: string;
  entries: DuplicateGroupEntry[];
  /** Vorausgewählter Master-Eintrag (Heuristik). */
  suggestedMasterId: number;
  epMin: number;
  epMax: number;
}

function dupGroupKey(name: string, unit: string, sektionId: string): string {
  return `${sektionId}::${libraryLookupKey(name, unit)}`;
}

function entryDate(p: LibraryPosition): number {
  const v = (p as { createdAt?: Date | string | null }).createdAt;
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Wählt den Master-Eintrag einer Duplikat-Gruppe nach folgender Heuristik:
 *  1. Globale Einträge (userId=null) gewinnen immer (dürfen nicht entfernt werden).
 *  2. Mehr Preis-Samples gewinnen.
 *  3. Status „freigegeben" vor „entwurf".
 *  4. Älterer Eintrag (kleinere createdAt) gewinnt.
 *  5. Stabil über kleinere ID.
 */
export function pickDuplicateMaster(items: LibraryPosition[]): LibraryPosition {
  if (items.length === 0) {
    throw new Error("pickDuplicateMaster: leere Liste");
  }
  const sorted = [...items].sort((a, b) => {
    const aGlobal = a.userId === null ? 1 : 0;
    const bGlobal = b.userId === null ? 1 : 0;
    if (aGlobal !== bGlobal) return bGlobal - aGlobal;
    const aSamples = (a.priceSamples ?? []).length;
    const bSamples = (b.priceSamples ?? []).length;
    if (aSamples !== bSamples) return bSamples - aSamples;
    const aFree = a.status === "freigegeben" ? 1 : 0;
    const bFree = b.status === "freigegeben" ? 1 : 0;
    if (aFree !== bFree) return bFree - aFree;
    const aDate = entryDate(a);
    const bDate = entryDate(b);
    if (aDate !== bDate) return aDate - bDate;
    return a.id - b.id;
  });
  return sorted[0];
}

/**
 * Findet Duplikat-Gruppen in einer Liste von Bibliotheks-Positionen.
 *
 * Bucketing nach (sektionId, libraryLookupKey(name, unit)). Gruppen mit
 * mindestens zwei Einträgen werden klassifiziert:
 *  - „exact"   : alle EPs identisch (auf 2 Nachkommastellen) UND Bezeichnungen
 *                bis auf Whitespace/Case identisch.
 *  - „possible": EP weicht ab oder Bezeichnung weicht nur durch Normalisierung
 *                ab (Groß-/Kleinschreibung, doppelte Leerzeichen).
 *
 * Einträge mit `aktiv === false` werden ignoriert (Soft-Delete).
 */
export function findLibraryDuplicateGroups(
  positions: LibraryPosition[],
  options?: { tolerance?: number },
): DuplicateGroup[] {
  const tolerance = options?.tolerance ?? DUPLICATE_EP_TOLERANCE;
  const buckets = new Map<string, LibraryPosition[]>();
  for (const p of positions) {
    if (p.aktiv === false) continue;
    const key = dupGroupKey(p.name, p.unit, p.sektionId);
    const arr = buckets.get(key) ?? [];
    arr.push(p);
    buckets.set(key, arr);
  }

  const groups: DuplicateGroup[] = [];
  for (const [key, items] of Array.from(buckets.entries())) {
    if (items.length < 2) continue;
    const eps = items.map((p) => Math.round(p.ep * 100) / 100);
    const epMin = Math.min(...eps);
    const epMax = Math.max(...eps);
    const namesNormalized = new Set(
      items.map((p) => p.name.trim().toLowerCase().replace(/\s+/g, " ")),
    );
    let kind: DuplicateGroupKind;
    if (epMin === epMax && namesNormalized.size === 1) {
      // Normalisierter Name + Einheit + Sektion + EP identisch → exakt.
      kind = "exact";
    } else {
      // Verdachts-Gruppe nur, wenn EP-Spanne innerhalb der Toleranz liegt
      // (oder identisch ist) – sonst sind es wahrscheinlich verschiedene
      // Leistungen mit gleichem Namen und werden ignoriert.
      const epBase = Math.max(epMin, 0.01);
      const relSpread = (epMax - epMin) / epBase;
      if (epMin === epMax || relSpread <= tolerance) {
        kind = "possible";
      } else {
        continue;
      }
    }
    const master = pickDuplicateMaster(items);
    groups.push({
      id: key,
      kind,
      sektionId: items[0].sektionId,
      name: items[0].name,
      unit: items[0].unit,
      entries: items.map((p) => ({
        position: p,
        sampleCount: (p.priceSamples ?? []).length,
      })),
      suggestedMasterId: master.id,
      epMin,
      epMax,
    });
  }

  groups.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "exact" ? -1 : 1;
    if (a.entries.length !== b.entries.length) return b.entries.length - a.entries.length;
    return a.name.localeCompare(b.name, "de");
  });
  return groups;
}

/**
 * Führt Preis-Samples mehrerer Einträge zusammen. Dedupliziert über
 * (datum, ep, menge, quelle) und behält die jüngsten 50.
 */
export function mergePriceSamples(
  ...lists: Array<PriceSample[] | null | undefined>
): PriceSample[] {
  const seen = new Set<string>();
  const out: PriceSample[] = [];
  for (const list of lists) {
    if (!list) continue;
    for (const s of list) {
      const k = `${s.datum ?? ""}|${s.ep}|${s.menge}|${s.quelle ?? ""}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(s);
    }
  }
  return out
    .sort((a, b) => (a.datum ?? "").localeCompare(b.datum ?? ""))
    .slice(-50);
}
